"use client";

import { useState, useCallback, useRef, useEffect, type KeyboardEvent, type DragEvent, type ClipboardEvent, type TouchEvent as ReactTouchEvent } from "react";
import { apiUpload } from "../lib/api";

interface PendingAttachment {
  id: string;
  filename: string;
  mimeType: string;
  url: string;
  uploading?: boolean;
}

interface Props {
  onSend: (text: string, attachmentIds?: string[]) => Promise<void>;
  disabled?: boolean;
  token: string | null;
}

export function MessageInput({ onSend, disabled, token }: Props) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasContent = text.trim().length > 0 || attachments.length > 0;

  const uploadFile = useCallback(async (file: File) => {
    if (!token) return;
    const tempId = `uploading-${Date.now()}-${Math.random()}`;
    const pending: PendingAttachment = {
      id: tempId,
      filename: file.name,
      mimeType: file.type || "application/octet-stream",
      url: "",
      uploading: true,
    };
    setAttachments(prev => [...prev, pending]);
    try {
      const result = await apiUpload<{ id: string; filename: string; mimeType: string; url: string }>(
        "/attachments",
        token,
        file,
      );
      setAttachments(prev =>
        prev.map(a => a.id === tempId ? { ...result, uploading: false } : a)
      );
    } catch (err) {
      console.warn("Upload failed:", err);
      setAttachments(prev => prev.filter(a => a.id !== tempId));
    }
  }, [token]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(f => uploadFile(f));
    e.target.value = "";
  }, [uploadFile]);

  const removeAttachment = useCallback((id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  }, []);

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    const readyAttachments = attachments.filter(a => !a.uploading);
    if ((!trimmed && readyAttachments.length === 0) || sending) return;

    setSending(true);
    try {
      const ids = readyAttachments.length > 0 ? readyAttachments.map(a => a.id) : undefined;
      await onSend(trimmed, ids);
      setText("");
      setAttachments([]);
      // Reset textarea height after clearing
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.overflowY = "hidden";
      }
    } catch {
      // ignore send errors
    } finally {
      setSending(false);
      if (window.matchMedia("(pointer: fine)").matches) {
        requestAnimationFrame(() => textareaRef.current?.focus());
      }
    }
  }, [text, attachments, sending, onSend]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (files) {
      Array.from(files).forEach(f => uploadFile(f));
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          uploadFile(file);
        }
      }
    }
  };

  // Auto-grow textarea height based on content, up to a max
  const MAX_HEIGHT = 120; // ~5 lines
  const autoGrow = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, MAX_HEIGHT) + "px";
    // Enable scrolling only when content exceeds max height
    ta.style.overflowY = ta.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
  }, []);

  useEffect(() => {
    autoGrow();
  }, [text, autoGrow]);

  // Prevent iOS Safari from scrolling parent containers when touching the input area
  // but allow scrolling inside the textarea itself
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const preventScroll = (e: globalThis.TouchEvent) => {
      const target = e.target as HTMLElement;
      // Allow touch scrolling inside the textarea when it has overflow content
      if (target === textareaRef.current) {
        const ta = textareaRef.current;
        if (ta && ta.scrollHeight > ta.clientHeight) {
          return; // let the textarea scroll naturally
        }
      }
      e.preventDefault();
    };
    el.addEventListener("touchmove", preventScroll, { passive: false });
    return () => el.removeEventListener("touchmove", preventScroll);
  }, []);

  // Counteract iOS scroll-into-view when textarea gets focus
  const handleFocus = useCallback(() => {
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  }, []);

  const isUploading = attachments.some(a => a.uploading);

  const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "/api";
  const resolveUrl = (url: string) => url.startsWith("/v1/") ? API_URL + url.slice(3) : url;

  return (
    <div
      ref={containerRef}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "0.65rem 0.75rem",
        borderTop: "1px solid var(--color-border)",
        background: dragOver ? "#f0f7ff" : "var(--color-bg)",
        flexShrink: 0,
        minWidth: 0,
        maxWidth: 768,
        marginLeft: "auto",
        marginRight: "auto",
        width: "100%",
        boxSizing: "border-box",
        transition: "background 0.15s",
        touchAction: "manipulation",
      }}
    >
      {/* Attachment previews */}
      {attachments.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.35rem",
            marginBottom: "0.4rem",
          }}
        >
          {attachments.map(a => {
            const isImage = a.mimeType.startsWith("image/");
            return (
              <div
                key={a.id}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.3rem",
                  padding: isImage ? 0 : "0.2rem 0.5rem",
                  background: isImage ? "transparent" : "var(--color-bg-tertiary)",
                  borderRadius: 4,
                  border: isImage ? "none" : "1px solid var(--color-border)",
                  fontSize: "0.75rem",
                  color: "var(--color-text-secondary)",
                  opacity: a.uploading ? 0.5 : 1,
                }}
              >
                {isImage && a.url ? (
                  <img
                    src={resolveUrl(a.url)}
                    alt={a.filename}
                    style={{
                      width: 40,
                      height: 40,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                ) : (
                  <span>{a.uploading ? "Uploading..." : a.filename}</span>
                )}
                <button
                  onClick={() => removeAttachment(a.id)}
                  style={{
                    position: isImage ? "absolute" : "relative",
                    top: isImage ? -4 : "auto",
                    right: isImage ? -4 : "auto",
                    width: 16,
                    height: 16,
                    padding: 0,
                    background: "var(--color-text-muted)",
                    color: "var(--color-btn-primary-text)",
                    border: "none",
                    borderRadius: "50%",
                    fontSize: "0.6rem",
                    lineHeight: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                  title="Remove"
                >
                  x
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Input row */}
      <div style={{ display: "flex", gap: "0.4rem", alignItems: "flex-end" }}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.txt,.md,.csv,.json"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={handleFocus}
          placeholder="Type a message..."
          disabled={disabled || sending}
          rows={1}
          style={{
            flex: 1,
            minWidth: 0,
            width: 0,
            resize: "none",
            overflow: "hidden", // overridden dynamically by autoGrow
            padding: "0.45rem 0.65rem",
            background: "var(--color-bg-tertiary)",
            color: "var(--color-text)",
            border: "1px solid var(--color-border-secondary)",
            borderRadius: 6,
            fontSize: "0.85rem",
            fontFamily: "inherit",
            lineHeight: 1.4,
            outline: "none",
          }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || sending || !token}
          style={{
            width: 32,
            height: 32,
            padding: 0,
            background: "transparent",
            color: "var(--color-text-muted)",
            border: "1px solid var(--color-border-secondary)",
            borderRadius: "50%",
            fontSize: "0.9rem",
            lineHeight: 1,
            cursor: "pointer",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="Attach file"
        >
          +
        </button>
        <button
          onClick={handleSend}
          disabled={disabled || sending || isUploading || !hasContent}
          style={{
            width: 32,
            height: 32,
            padding: 0,
            background: hasContent && !isUploading ? "var(--color-btn-primary-bg)" : "var(--color-border-secondary)",
            color: hasContent && !isUploading ? "var(--color-btn-primary-text)" : "var(--color-text-tertiary)",
            border: "none",
            borderRadius: "50%",
            fontSize: "1rem",
            lineHeight: 1,
            cursor: hasContent && !isUploading ? "pointer" : "default",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="Send"
        >
          ↑
        </button>
      </div>
    </div>
  );
}
